<!-- Quick Links Table with Row Expansion -->
<div
  class="quick-links-table"
  *ngIf="data && data.data && data.data.length > 0"
>
  <!-- Main Data Table with Row Expansion -->
  <p-table
    #quickLinksTable
    [value]="data.data || []"
    [loading]="loading"
    [paginator]="true"
    [rows]="data.pagination?.pageSize || 25"
    [totalRecords]="data.pagination?.totalItems || 0"
    [lazy]="true"
    [sortMode]="'single'"
    [showCurrentPageReport]="true"
    currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
    [rowsPerPageOptions]="pageSizeOptions"
    [scrollHeight]="scrollHeight"
    [scrollable]="true"
    scrollDirection="both"
    size="small"
    responsiveLayout="scroll"
    dataKey="id"
    [rowExpandMode]="'single'"
    [expandedRowKeys]="expandedRows"
    (onSort)="onSort($event)"
    (onPage)="onPageChange($event)"
    (onRowExpand)="onRowExpand($event)"
    (onRowCollapse)="onRowCollapse($event)"
    styleClass="p-datatable-quicklinks"
  >
    <!-- Table Header -->
    <ng-template pTemplate="header">
      <tr>
        <!-- Expand/Collapse Column (Frozen Left) -->
        <th
          style="width: 2.5rem"
          class="text-center"
          pFrozenColumn
          [frozen]="true"
          alignFrozen="left"
        >
          <!-- Empty header for expand toggle -->
        </th>

        <!-- ID Column (Frozen Left) -->
        <th
          style="width: auto; min-width: 5rem"
          pFrozenColumn
          [frozen]="true"
          alignFrozen="left"
        >
          ID
        </th>

        <!-- Name Column -->
        <th [pSortableColumn]="'name'" style="width: auto; min-width: 10rem">
          Name
          <p-sortIcon [field]="'name'" />
        </th>

        <!-- Slug Column -->
        <th style="width: auto; min-width: 10rem">Slug</th>

        <!-- Action Type Column -->
        <th style="width: auto; min-width: 8rem">Action Type</th>

        <!-- Status Column -->
        <th [pSortableColumn]="'status'" style="width: auto; min-width: 7rem">
          Status
          <p-sortIcon [field]="'status'" />
        </th>

        <!-- Enabled Column -->
        <th style="width: auto; min-width: 6rem">Enabled</th>

        <!-- Usage Column -->
        <th
          [pSortableColumn]="'currentUsageCount'"
          style="width: auto; min-width: 10rem"
        >
          Usage
          <p-sortIcon [field]="'currentUsageCount'" />
        </th>

        <!-- One-time Use Column -->
        <!-- COMMENTED OUT: API not ready yet
        <th style="width: auto; min-width: 8rem">One-time Use</th>
        -->

        <!-- Actions Column (Frozen Right) -->
        <th
          style="width: 3rem"
          pFrozenColumn
          [frozen]="true"
          alignFrozen="right"
        >
          Actions
        </th>
      </tr>
    </ng-template>

    <!-- Table Body -->
    <ng-template
      pTemplate="body"
      let-item
      let-rowIndex="rowIndex"
      let-expanded="expanded"
    >
      <tr
        class="quick-link-row"
        [class.deleted-row]="item.status === 2"
        [class.expanded]="expanded"
        (click)="toggleRowExpansion(item)"
      >
        <!-- Row Expansion Toggle (Frozen Left) -->
        <td
          class="text-center expand-column"
          pFrozenColumn
          [frozen]="true"
          alignFrozen="left"
          (click)="$event.stopPropagation()"
        >
          <!-- No label for expand on mobile -->
          <p-button
            [pRowToggler]="item"
            type="button"
            pRipple
            [text]="true"
            [rounded]="true"
            [plain]="true"
            size="small"
            styleClass="text-gray-400 hover:text-gray-600"
            [icon]="expanded ? 'pi pi-chevron-down' : 'pi pi-chevron-right'"
            [disabled]="loading"
            pTooltip="Click to expand/collapse row details"
            tooltipPosition="top"
          />
        </td>

        <!-- ID Column (Frozen Left) -->
        <td
          class="id-column"
          pFrozenColumn
          [frozen]="true"
          alignFrozen="left"
          (click)="$event.stopPropagation()"
        >
          <span class="p-column-title">ID</span>
          <a
            href="javascript:;"
            class="id-link"
            (click)="rowAction.emit({ action: 'edit', item })"
          >
            #{{ item?.id }}
          </a>
        </td>

        <!-- Name -->
        <td>
          <span class="p-column-title">Name</span>
          <div class="flex flex-column gap-1">
            <span class="font-medium">{{ item.name }}</span>
            <span
              *ngIf="isExpired(item)"
              class="text-xs text-red-500 font-medium"
            >
              <i class="pi pi-exclamation-triangle mr-1"></i>
              Expired
            </span>
          </div>
        </td>

        <!-- Slug -->
        <td>
          <span class="p-column-title">Slug</span>
          <code class="text-sm">{{ item.slug }}</code>
        </td>

        <!-- Action Type -->
        <td>
          <span class="p-column-title">Action Type</span>
          <div class="flex align-items-center gap-1">
            <i [class]="getActionTypeIcon(item.actionType)"></i>
            <span>{{ getActionTypeLabel(item.actionType) }}</span>
          </div>
        </td>

        <!-- Status -->
        <td>
          <span class="p-column-title">Status</span>
          <p-tag
            [value]="getStatusLabel(item.status)"
            [severity]="getStatusSeverity(item.status)"
            [rounded]="true"
            [styleClass]="'p-tag-' + getStatusSeverity(item.status)"
          />
        </td>

        <!-- Enabled Toggle -->
        <td (click)="$event.stopPropagation()">
          <span class="p-column-title">Enabled</span>
          <p-inputSwitch
            [ngModel]="getStatusChecked(item.status)"
            (onChange)="onToggleStatus(item, $event)"
            [disabled]="loading || item.status === 2"
            [pTooltip]="
              item.status === 2
                ? 'Deleted links cannot be toggled'
                : item.status === 1
                  ? 'Click to disable'
                  : 'Click to enable'
            "
            tooltipPosition="top"
          />
        </td>

        <!-- Usage -->
        <td>
          <span class="p-column-title">Usage</span>
          <span
            class="usage-value"
            [ngClass]="getUsageColorClass(item)"
            [pTooltip]="getUsageTooltip(item)"
            tooltipPosition="top"
          >
            {{ getUsageDisplay(item) }}
            <!-- Status icon for limited use links -->
            <i
              *ngIf="item.usageLimitType === 1"
              [ngClass]="[getUsageIcon(item), getUsageColorClass(item)]"
              class="ml-1"
            ></i>
          </span>
        </td>

        <!-- One-time Use Checkbox -->
        <!-- COMMENTED OUT: API not ready yet
        <td class="text-center">
          <span class="p-column-title">One-time Use</span>
          <p-checkbox
            [ngModel]="isSingleUse(item)"
            (onChange)="onToggleOneTimeUse(item, $event)"
            [binary]="true"
            [disabled]="loading || item.status === 2"
            [pTooltip]="
              item.status === 2
                ? 'Deleted links cannot be modified'
                : isSingleUse(item)
                  ? 'Click to allow unlimited uses'
                  : 'Click to make this a one-time use link'
            "
            tooltipPosition="top"
          />
        </td>
        -->

        <!-- Row Actions Menu (Frozen Right) -->
        <td
          class="actions-column"
          pFrozenColumn
          [frozen]="true"
          alignFrozen="right"
          (click)="$event.stopPropagation()"
        >
          <span class="p-column-title">Actions</span>
          <p-button
            icon="pi pi-ellipsis-v"
            [text]="true"
            size="small"
            [disabled]="loading"
            (onClick)="showContextMenu($event, item)"
            pTooltip="Actions"
            tooltipPosition="left"
          />
        </td>
      </tr>
    </ng-template>

    <!-- Row Expansion Template -->
    <ng-template pTemplate="expandedrow" let-item>
      <tr class="expanded-row">
        <td [attr.colspan]="9" class="expanded-row-cell">
          <div class="expanded-row-content">
            <!-- Quick Actions Section -->
            <div class="quick-actions-section">
              <p-button
                label="Edit"
                icon="pi pi-pencil"
                size="small"
                severity="secondary"
                [outlined]="true"
                (click)="rowAction.emit({ action: 'edit', item })"
              />
              <p-button
                label="View Metrics"
                icon="pi pi-chart-line"
                size="small"
                severity="secondary"
                [outlined]="true"
                (click)="rowAction.emit({ action: 'viewMetrics', item })"
              />
            </div>

            <!-- Detailed Usage Statistics (only for limited use) -->
            <div
              class="detail-section"
              *ngIf="item.usageLimitType === 1 && item.maxUses"
            >
              <div class="section-header">Detailed Usage Statistics</div>
              <dl class="detail-list">
                <div class="detail-item">
                  <dt>Max Uses</dt>
                  <dd>{{ item.maxUses }}</dd>
                </div>
                <div class="detail-item">
                  <dt>Current Usage</dt>
                  <dd>{{ item.currentUsageCount }}</dd>
                </div>
                <div class="detail-item">
                  <dt>Available Uses</dt>
                  <dd>
                    <span [ngClass]="getUsageColorClass(item)">
                      {{ getAvailableUses(item) }} remaining
                    </span>
                  </dd>
                </div>
                <div class="detail-item">
                  <dt>Usage Percentage</dt>
                  <dd>
                    <span [ngClass]="getUsageColorClass(item)">
                      {{ getUsagePercentage(item) }}%
                    </span>
                  </dd>
                </div>
                <div class="detail-item detail-item-stacked">
                  <dt>Status</dt>
                  <dd [ngClass]="getUsageColorClass(item)">
                    <i [ngClass]="getUsageIcon(item)" class="mr-1"></i>
                    <span *ngIf="isUsageLimitReached(item)">
                      Usage limit reached - Link cannot be used
                    </span>
                    <span
                      *ngIf="
                        isApproachingLimit(item) && !isUsageLimitReached(item)
                      "
                    >
                      Approaching limit - {{ getAvailableUses(item) }} uses
                      remaining
                    </span>
                    <span
                      *ngIf="
                        !isApproachingLimit(item) && !isUsageLimitReached(item)
                      "
                    >
                      Active - {{ getAvailableUses(item) }} uses available
                    </span>
                  </dd>
                </div>
              </dl>
            </div>

            <!-- Redirect Configuration -->
            <div class="detail-section" *ngIf="item.redirect">
              <div class="section-header">Redirect Configuration</div>
              <dl class="detail-list">
                <div class="detail-item">
                  <dt>Redirect Type</dt>
                  <dd>{{ getRedirectTypeLabel(item.redirect.type) }}</dd>
                </div>
                <div
                  class="detail-item detail-item-stacked"
                  *ngIf="item.redirect.message"
                >
                  <dt>Message</dt>
                  <dd>{{ item.redirect.message }}</dd>
                </div>
                <div
                  class="detail-item detail-item-stacked"
                  *ngIf="item.redirect.customUrl"
                >
                  <dt>Custom URL</dt>
                  <dd class="url-container">
                    <span
                      class="url-truncate"
                      [pTooltip]="item.redirect.customUrl"
                      tooltipPosition="top"
                    >
                      {{ item.redirect.customUrl }}
                    </span>
                    <button
                      type="button"
                      class="copy-url-button"
                      (click)="copyToClipboard(item.redirect.customUrl!)"
                      pTooltip="Copy URL to clipboard"
                      tooltipPosition="top"
                    >
                      <i class="pi pi-copy"></i>
                    </button>
                  </dd>
                </div>
                <div
                  class="detail-item detail-item-stacked"
                  *ngIf="item.redirect.utmParams"
                >
                  <dt>UTM Parameters</dt>
                  <dd>{{ parseUtmParams(item.redirect.utmParams) }}</dd>
                </div>
              </dl>
            </div>

            <!-- Security & Access -->
            <div class="detail-section">
              <div class="section-header">Security & Access</div>
              <dl class="detail-list">
                <div class="detail-item">
                  <dt>Requires Login</dt>
                  <dd>
                    <span
                      *ngIf="item.requiresLogin"
                      class="text-orange-600 font-semibold"
                    >
                      <i class="pi pi-lock mr-1"></i>
                      Yes
                    </span>
                    <span *ngIf="!item.requiresLogin" class="text-green-600">
                      <i class="pi pi-lock-open mr-1"></i>
                      No
                    </span>
                  </dd>
                </div>
                <div class="detail-item detail-item-stacked">
                  <dt>URL</dt>
                  <dd class="url-container">
                    <span
                      class="url-truncate"
                      [pTooltip]="item.templateUrl"
                      tooltipPosition="top"
                    >
                      {{ item.templateUrl }}
                    </span>
                    <button
                      type="button"
                      class="copy-url-button"
                      (click)="copyToClipboard(item.templateUrl)"
                      pTooltip="Copy URL to clipboard"
                      tooltipPosition="top"
                    >
                      <i class="pi pi-copy"></i>
                    </button>
                  </dd>
                </div>
              </dl>
            </div>

            <!-- Timeline -->
            <div class="detail-section">
              <div class="section-header">Timeline</div>
              <dl class="detail-list">
                <div class="detail-item">
                  <dt>Created At</dt>
                  <dd>{{ formatDate(item.createdUtc) }}</dd>
                </div>
                <div class="detail-item">
                  <dt>Updated At</dt>
                  <dd>{{ formatDate(item.updatedUtc) }}</dd>
                </div>
                <div class="detail-item" *ngIf="item.lastUsedUtc">
                  <dt>Last Used</dt>
                  <dd>{{ formatDate(item.lastUsedUtc) }}</dd>
                </div>
                <div class="detail-item" *ngIf="item.expiredUtc">
                  <dt>Expires At</dt>
                  <dd>
                    <span
                      [class.text-red-600]="isExpired(item)"
                      [class.font-semibold]="isExpired(item)"
                    >
                      <i
                        *ngIf="isExpired(item)"
                        class="pi pi-exclamation-triangle mr-1"
                      ></i>
                      {{ formatDate(item.expiredUtc) }}
                      <span *ngIf="isExpired(item)">(Expired)</span>
                    </span>
                  </dd>
                </div>
                <div class="detail-item" *ngIf="!item.expiredUtc">
                  <dt>Expires At</dt>
                  <dd class="text-green-600">
                    <i class="pi pi-infinity mr-1"></i>
                    Never expires
                  </dd>
                </div>
              </dl>
            </div>
          </div>
        </td>
      </tr>
    </ng-template>

    <!-- Empty State -->
    <ng-template pTemplate="emptymessage">
      <tr>
        <td [attr.colspan]="9" class="text-center p-4">
          <div class="flex flex-column align-items-center gap-3">
            <i class="pi pi-link text-4xl text-400"></i>
            <span class="text-lg font-medium text-600"
              >No quick links found</span
            >
            <span class="text-sm text-500"
              >Try adjusting your filters or search criteria</span
            >
          </div>
        </td>
      </tr>
    </ng-template>

    <!-- Loading State -->
    <ng-template pTemplate="loadingbody">
      <app-table-skeleton-rows [rows]="5" [columns]="skeletonColumns" />
    </ng-template>
  </p-table>
</div>

<!-- Empty State when no data -->
<div
  *ngIf="!loading && (!data || !data.data || data.data.length === 0)"
  class="text-center p-4"
>
  <p class="text-gray-500">No quick links found.</p>
</div>

<!-- Context Menu -->
<p-menu #contextMenu [model]="rowMenuItems" [popup]="true" appendTo="body" />
<!-- Force recompilation -->
